import React, { useState, useEffect, useRef } from "react";
import { format, parseISO } from "date-fns";
import GaugeChart from "react-gauge-chart";
import { EditSecondaryIdModal } from "./modals";
import { ABOUT_YOU } from "../../Constants/Routes";
import AppConstants from "../../Constants/AppConstants";
import { S_CURRENT_MEMBER_IN_MARKETPLACE } from "../../Communication/Constants";

// Add styles for gauge chart text
const gaugeChartStyles = `
  .branch-gauge-chart svg text {
    font-size: 28px !important;
    font-weight: 700 !important;
  }
`;

// Helper function to get score color and rating
const getScoreDetails = (score) => {
  if (!score || score === "—") return { color: "#6c757d", rating: "N/A" };

  const numScore = parseInt(score);

  if (numScore >= 800) {
    return { color: "#007bff", rating: "VERY GOOD" };
  } else if (numScore >= 740) {
    return { color: "#28a745", rating: "GOOD" };
  } else if (numScore >= 670) {
    return { color: "#ffc107", rating: "FAIR" };
  } else if (numScore >= 580) {
    return { color: "#fd7e14", rating: "POOR" };
  } else {
    return { color: "#dc3545", rating: "VERY POOR" };
  }
};

// Credit Details Section
export const CreditDetailsSection = ({
  applicant,
  showCreditDetails,
  setShowCreditDetails,
}) => {
  return (
    <div className="mb-4">
      {/* Inject gauge chart styles */}
      <style>{gaugeChartStyles}</style>
      <div
        className="d-flex align-items-center mb-3"
        style={{ cursor: "pointer" }}
        onClick={() => setShowCreditDetails(!showCreditDetails)}
      >
        <span
          style={{
            color: "#e91e63",
            fontSize: "14px",
            fontWeight: "600",
            marginRight: "8px",
          }}
        >
          Credit / FICO
        </span>
        <i
          className={`fas fa-chevron-${showCreditDetails ? "down" : "right"}`}
          style={{ color: "#e91e63", fontSize: "12px" }}
        ></i>
      </div>

      {showCreditDetails && (
        <div className="[ details-section ] [ mb-3 ]">
          <div className="[ row ] [ details-section__header">
            <div className="[ col-12 ]">
              <h3 className="[ d-flex justify-content-between align-items-center ]">
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#495057",
                    textTransform: "uppercase",
                  }}
                >
                  EQUIFAX
                </span>
                <a
                  href="#"
                  style={{
                    fontSize: "12px",
                    color: "#007bff",
                    marginLeft: "8px",
                    textDecoration: "none",
                  }}
                >
                  See Details →
                </a>
              </h3>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-sm-12 col-lg-6">
              <span
                style={{
                  fontSize: "12px",
                  color: "#6c757d",
                  fontWeight: "600",
                }}
              >
                Status:
              </span>
              <span
                style={{
                  fontSize: "12px",
                  color: "#28a745",
                  marginLeft: "8px",
                  fontWeight: "600",
                }}
              >
                CURRENT
              </span>
            </div>
            <div className="col-sm-12 col-lg-6">
              <span
                style={{
                  fontSize: "12px",
                  color: "#6c757d",
                  fontWeight: "600",
                }}
              >
                Last update:
              </span>
              <span
                style={{
                  fontSize: "12px",
                  color: "#2c3e50",
                  marginLeft: "8px",
                }}
              >
                {applicant?.creditBanking?.beaconScoreDate
                  ? format(
                      parseISO(applicant.creditBanking.beaconScoreDate),
                      "MMM dd, yyyy"
                    )
                  : "—"}
              </span>
            </div>
          </div>

          {/* FICO and BNI Scores with Semi-Circular Gauges */}
          <div className="row mb-4">
            <div className="col-6">
              <div className="text-center mb-2">
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#6c757d",
                  }}
                >
                  FICO
                </span>
              </div>
              <div
                style={{
                  width: "100%",
                  maxWidth: "180px",
                  margin: "0 auto",
                  position: "relative",
                }}
              >
                <GaugeChart
                  className="branch-gauge-chart"
                  id="fico-gauge"
                  nrOfLevels={5}
                  colors={[
                    "#dc3545",
                    "#fd7e14",
                    "#ffc107",
                    "#28a745",
                    "#007bff",
                  ]}
                  arcWidth={0.1}
                  percent={
                    applicant?.creditBanking?.beaconScore &&
                    applicant?.creditBanking?.beaconScore > 299
                      ? (applicant.creditBanking.beaconScore - 300) /
                        (900 - 300)
                      : 0
                  }
                  textColor="#2c3e50"
                  needleColor="#495057"
                  needleBaseColor="#495057"
                  hideText={false}
                  formatTextValue={() =>
                    applicant?.creditBanking?.beaconScore || "—"
                  }
                  animate={false}
                />
              </div>
              <div className="text-center" style={{ marginTop: "-10px" }}>
                <span
                  style={{
                    fontSize: "11px",
                    color: getScoreDetails(
                      applicant?.creditBanking?.beaconScore
                    ).color,
                    fontWeight: "600",
                    textTransform: "uppercase",
                  }}
                >
                  {
                    getScoreDetails(applicant?.creditBanking?.beaconScore)
                      .rating
                  }
                </span>
              </div>
            </div>
            <div className="col-6">
              <div className="text-center mb-2">
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#6c757d",
                  }}
                >
                  BNI
                </span>
              </div>
              <div
                style={{
                  width: "100%",
                  maxWidth: "180px",
                  margin: "0 auto",
                  position: "relative",
                }}
              >
                <GaugeChart
                  className="branch-gauge-chart"
                  id="bni-gauge"
                  nrOfLevels={5}
                  colors={[
                    "#dc3545",
                    "#fd7e14",
                    "#ffc107",
                    "#28a745",
                    "#007bff",
                  ]}
                  arcWidth={0.1}
                  percent={
                    applicant?.creditBanking?.bni &&
                    applicant?.creditBanking?.bni > 299
                      ? (applicant.creditBanking.bni - 300) / (900 - 300)
                      : 0
                  }
                  textColor="#2c3e50"
                  needleColor="#495057"
                  needleBaseColor="#495057"
                  hideText={false}
                  formatTextValue={() => applicant?.creditBanking?.bni || "—"}
                  animate={false}
                />
              </div>
              <div className="text-center" style={{ marginTop: "-10px" }}>
                <span
                  style={{
                    fontSize: "11px",
                    color: getScoreDetails(applicant?.creditBanking?.bni).color,
                    fontWeight: "600",
                    textTransform: "uppercase",
                  }}
                >
                  {getScoreDetails(applicant?.creditBanking?.bni).rating}
                </span>
              </div>
            </div>
          </div>

          {/* Credit Factors */}
          <div>
            <h6
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#495057",
                marginBottom: "16px",
              }}
            >
              Credit Factors
            </h6>

            <div style={{ marginBottom: "16px" }}>
              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  marginBottom: "2px",
                }}
              >
                <span style={{ fontSize: "12px", color: "#495057" }}>
                  Credit utilization:
                </span>
                <div className="d-flex align-items-center">
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      marginRight: "12px",
                    }}
                  >
                    24%
                  </span>
                  <div
                    style={{
                      width: "80px",
                      height: "18px",
                      background:
                        "linear-gradient(to right, rgba(255, 193, 7, 0.2), #ffc107)",
                    }}
                  ></div>
                </div>
              </div>

              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  marginBottom: "2px",
                }}
              >
                <span style={{ fontSize: "12px", color: "#495057" }}>
                  Payment history:
                </span>
                <div className="d-flex align-items-center">
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      marginRight: "12px",
                    }}
                  >
                    100%
                  </span>
                  <div
                    style={{
                      width: "80px",
                      height: "18px",
                      background:
                        "linear-gradient(to right, rgba(40, 167, 69, 0.2), #28a745)",
                    }}
                  ></div>
                </div>
              </div>

              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  marginBottom: "2px",
                }}
              >
                <span style={{ fontSize: "12px", color: "#495057" }}>
                  Derogatory Marks:
                </span>
                <div className="d-flex align-items-center">
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      marginRight: "12px",
                    }}
                  >
                    1
                  </span>
                  <div
                    style={{
                      width: "80px",
                      height: "18px",
                      background:
                        "linear-gradient(to right, rgba(255, 193, 7, 0.2), #ffc107)",
                    }}
                  ></div>
                </div>
              </div>

              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  marginBottom: "2px",
                }}
              >
                <span style={{ fontSize: "12px", color: "#495057" }}>
                  Age of credit history:
                </span>
                <div className="d-flex align-items-center">
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      marginRight: "12px",
                    }}
                  >
                    32 yrs 7 mos
                  </span>
                  <div
                    style={{
                      width: "80px",
                      height: "18px",
                      background:
                        "linear-gradient(to right, rgba(40, 167, 69, 0.2), #28a745)",
                    }}
                  ></div>
                </div>
              </div>

              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  marginBottom: "2px",
                }}
              >
                <span style={{ fontSize: "12px", color: "#495057" }}>
                  Hard inquiries:
                </span>
                <div className="d-flex align-items-center">
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      marginRight: "12px",
                    }}
                  >
                    2
                  </span>
                  <div
                    style={{
                      width: "80px",
                      height: "18px",
                      background:
                        "linear-gradient(to right, rgba(40, 167, 69, 0.2), #28a745)",
                    }}
                  ></div>
                </div>
              </div>

              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  marginBottom: "2px",
                }}
              >
                <span style={{ fontSize: "12px", color: "#495057" }}>
                  Reported accounts:
                </span>
                <div className="d-flex align-items-center">
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      marginRight: "12px",
                    }}
                  >
                    14
                  </span>
                  <div
                    style={{
                      width: "80px",
                      height: "18px",
                      background:
                        "linear-gradient(to right, rgba(0, 123, 255, 0.2), #007bff)",
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
          <div className="[ divider ]" />
          {/* Request new report button */}
          <div className="text-center mt-4">
            <button
              className="btn"
              style={{
                fontSize: "14px",
                padding: "10px 32px",
                borderRadius: "4px",
                backgroundColor: "#f8f9fa",
                color: "#6c757d",
                border: "1px solid #dee2e6",
                fontWeight: "500",
              }}
            >
              Request new report
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// KYC Details Section
export const KycDetailsSection = ({
  applicant,
  showKycDetails,
  setShowKycDetails,
  history,
}) => {
  return (
    <div className="mb-4">
      <div
        className="d-flex align-items-center mb-3"
        style={{ cursor: "pointer" }}
        onClick={() => setShowKycDetails(!showKycDetails)}
      >
        <span
          style={{
            color: "#e91e63",
            fontSize: "14px",
            fontWeight: "600",
            marginRight: "8px",
          }}
        >
          Digital KYC
        </span>
        <i
          className={`fas fa-chevron-${showKycDetails ? "down" : "right"}`}
          style={{ color: "#e91e63", fontSize: "12px" }}
        ></i>
      </div>

      {showKycDetails && (
        <div className="[ details-section ] [ mb-3 ]">
          <div className="[ row ] [ details-section__header">
            <div className="[ col-12 ]">
              <h3 className="[ d-flex justify-content-between align-items-center ]">
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#495057",
                    textTransform: "uppercase",
                  }}
                >
                  INSTNT DIGITAL KYC
                </span>
                <a
                  href="#"
                  style={{
                    fontSize: "12px",
                    color: "#007bff",
                    marginLeft: "8px",
                    textDecoration: "none",
                  }}
                >
                  See Details →
                </a>
              </h3>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-sm-12 col-lg-6">
              <span
                style={{
                  fontSize: "12px",
                  color: "#6c757d",
                  fontWeight: "600",
                }}
              >
                Status:
              </span>
              <span
                style={{
                  fontSize: "12px",
                  color: "#28a745",
                  marginLeft: "8px",
                  fontWeight: "600",
                }}
              >
                CURRENT
              </span>
            </div>
            <div className="col-sm-12 col-lg-6">
              <span
                style={{
                  fontSize: "12px",
                  color: "#6c757d",
                  fontWeight: "600",
                }}
              >
                Last update:
              </span>
              <span
                style={{
                  fontSize: "12px",
                  color: "#2c3e50",
                  marginLeft: "8px",
                }}
              >
                {applicant?.regulatory?.idVerificationDate
                  ? format(
                      parseISO(applicant.regulatory.idVerificationDate),
                      "MMM dd, yyyy"
                    )
                  : "—"}
              </span>
            </div>
          </div>

          <div>
            <h6
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#495057",
                marginBottom: "16px",
              }}
            >
              KYC Factors
            </h6>

            <div style={{ marginBottom: "16px" }}>
              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  marginBottom: "2px",
                }}
              >
                <span style={{ fontSize: "12px", color: "#495057" }}>
                  Auto-adjudication:
                </span>
                <div className="d-flex align-items-center">
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      marginRight: "12px",
                    }}
                  >
                    {applicant?.latestInstntDecision}
                  </span>
                  <div
                    style={{
                      width: "80px",
                      height: "18px",
                      background: `linear-gradient(to right, rgba(255, 193, 7, 0.2), ${
                        applicant?.regulatory?.latestInstntDecision === "ACCEPT"
                          ? "#28a745"
                          : "#ffc107"
                      })`,
                    }}
                  ></div>
                </div>
              </div>
              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  marginBottom: "2px",
                }}
              >
                <span style={{ fontSize: "12px", color: "#495057" }}>
                  KYC Override:
                </span>
                <div className="d-flex align-items-center">
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      marginRight: "12px",
                    }}
                  >
                    {applicant?.kycOverride ? "ACCEPT" : "None"}
                  </span>
                  <div
                    style={{
                      width: "80px",
                      height: "18px",
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
          <div className="[ divider ]" />
          {/* Request new report button */}
          <div className="text-center mt-4">
            <button
              className="btn btn-outline-secondary"
              style={{
                fontSize: "14px",
                padding: "8px 24px",
                borderRadius: "4px",
                backgroundColor: "#e91e63",
                color: "white",
                border: "none",
                cursor: "pointer",
              }}
              onClick={() => {
                sessionStorage.setItem(S_CURRENT_MEMBER_IN_MARKETPLACE, "true");
                history.push(
                  ABOUT_YOU + "?" + AppConstants.URL_FLAG.KYC_REPORT
                );
              }}
            >
              Request new report
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Expiry Warning Modal Component
const ExpiryWarningModal = ({ show, onClose, idType, applicant }) => {
  if (!show) return null;

  return (
    <div
      className="modal-backdrop"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 1050,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        className="modal-content"
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          width: "90%",
          maxWidth: "400px",
          overflow: "hidden",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Warning Header */}
        <div
          style={{
            backgroundColor: "#ffc107",
            padding: "16px",
            textAlign: "center",
          }}
        >
          <span
            style={{
              fontSize: "32px",
              fontWeight: "bold",
              color: "white",
            }}
          >
            !
          </span>
        </div>

        {/* Modal Body */}
        <div style={{ padding: "24px" }}>
          <h5
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#2c3e50",
              marginBottom: "16px",
              textAlign: "center",
            }}
          >
            Missing or Expired Info
          </h5>
          <p
            style={{
              fontSize: "14px",
              color: "#6c757d",
              marginBottom: "8px",
            }}
          >
            It appears {applicant?.member?.name} may have missing or expired
            info.
          </p>
          <p
            style={{
              fontSize: "14px",
              color: "#6c757d",
              marginBottom: "4px",
            }}
          >
            Our records show:
          </p>
          <p
            style={{
              fontSize: "14px",
              color: "#6c757d",
              marginBottom: "4px",
              fontWeight: "600",
              paddingBottom: "16px",
            }}
          >
            - {idType === "1" ? "drivers license" : "passport"} on file,
          </p>
          <p
            style={{
              fontSize: "14px",
              color: "#6c757d",
              marginBottom: "24px",
            }}
          >
            has expired.
          </p>

          {/* OK Button */}
          <button
            onClick={onClose}
            style={{
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              padding: "10px 48px",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
              width: "100%",
              maxWidth: "200px",
              display: "block",
              margin: "0 auto",
            }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// Secondary ID Section
export const SecondaryIdSection = ({
  applicant,
  showSecondaryIdDetails,
  setShowSecondaryIdDetails,
  provinceList,
  idTypes,
  finInfo,
  handleSecondaryIdSaveClick,
  selectedMember,
  doFileList,
  doUploadFile,
}) => {
  const [showSecondaryIdEditModal, setShowSecondaryIdEditModal] = useState(
    false
  );
  const [showExpiryWarningModal, setShowExpiryWarningModal] = useState(false);

  // Hidden file input ref for direct file selection
  const fileInputRef = useRef(null);

  // Check if ID is expired on component mount
  useEffect(() => {
    if (
      applicant?.member?.expiryDate1 &&
      parseISO(applicant.member.expiryDate1) < new Date()
    ) {
      setShowExpiryWarningModal(true);
    }
  }, [applicant]);

  const handleSecondaryIdModalClose = () => {
    setShowSecondaryIdEditModal(false);
  };

  const handleSecondaryIdSaveClickWrapper = (secondaryIdFormData) => {
    handleSecondaryIdSaveClick(secondaryIdFormData, () => {
      setShowSecondaryIdEditModal(false);
    });
  };

  // Lookup province name from provinceList using placeOfIssuance1
  const getProvinceName = (code) => {
    if (!code || !provinceList || provinceList.length === 0) {
      return "—";
    }
    const province = provinceList.find((item) => item.code === code);
    return province ? province.description : code;
  };

  return (
    <div className="mb-4">
      <div
        className="d-flex align-items-center mb-3"
        style={{ cursor: "pointer" }}
        onClick={() => setShowSecondaryIdDetails(!showSecondaryIdDetails)}
      >
        <span
          style={{
            color: "#e91e63",
            fontSize: "14px",
            fontWeight: "600",
            marginRight: "8px",
          }}
        >
          Secondary ID
        </span>
        <i
          className={`fas fa-chevron-${
            showSecondaryIdDetails ? "down" : "right"
          }`}
          style={{ color: "#e91e63", fontSize: "12px" }}
        ></i>
      </div>

      {showSecondaryIdDetails && (
        <div className="[ details-section ] [ mb-3 ]">
          <div className="[ row ] [ details-section__header">
            <div className="[ col-12 ]">
              <h3 className="[ d-flex justify-content-between align-items-center ]">
                ID TYPE:{" "}
                {applicant?.member?.idType1 === "1"
                  ? "Drivers License"
                  : "Passport"}
                <button
                  type="button"
                  className="[ btn edit-btn ]"
                  aria-label="edit"
                  onClick={() => setShowSecondaryIdEditModal(true)}
                >
                  <span className="[ edit-icon ]"></span>
                  <span className="[ edit-btn__label ]">Edit</span>
                </button>
              </h3>
            </div>
          </div>

          <div
            className="[ row ] [ details-section__item ]"
            style={{
              fontSize: "12px",
              fontWeight: "400",
            }}
          >
            <div className="col-6">
              <span>State/Province/Territory:</span>
            </div>
            <div className="col-4">
              <span style={{ fontSize: "12px", fontWeight: "600" }}>
                {getProvinceName(applicant?.member?.placeOfIssuance1)}
              </span>
            </div>
            <div className="col-2">&nbsp;</div>
          </div>
          <div
            className="[ row ] [ details-section__item ]"
            style={{
              fontSize: "12px",
              fontWeight:
                !applicant?.member?.issueDate1 ||
                !applicant?.member?.expiryDate1 ||
                parseISO(applicant.member.expiryDate1) < new Date()
                  ? "600"
                  : "400",
              color:
                !applicant?.member?.issueDate1 ||
                !applicant?.member?.expiryDate1 ||
                parseISO(applicant.member.expiryDate1) < new Date()
                  ? "#ffc107"
                  : "#2c3e50",
            }}
          >
            <div className="col-6">Issued Date:</div>
            <div className="col-4" style={{ fontWeight: "600" }}>
              {applicant?.member?.issueDate1
                ? format(parseISO(applicant.member.issueDate1), "MMM dd, yyyy")
                : "—"}{" "}
            </div>
            <div className="col-2">
              {!applicant?.member?.issueDate1 ||
              !applicant?.member?.expiryDate1 ||
              parseISO(applicant.member.expiryDate1) < new Date()
                ? "⚠"
                : ""}
            </div>
          </div>
          <div
            className="[ row ] [ details-section__item ]"
            style={{
              fontSize: "12px",
              fontWeight:
                !applicant?.member?.expiryDate1 ||
                parseISO(applicant.member.expiryDate1) < new Date()
                  ? "600"
                  : "400",
              color:
                !applicant?.member?.expiryDate1 ||
                parseISO(applicant.member.expiryDate1) < new Date()
                  ? "#ffc107"
                  : "#2c3e50",
            }}
          >
            <div className="col-6">Expiry Date:</div>
            <div className="col-4" style={{ fontWeight: "600" }}>
              {applicant?.member?.expiryDate1
                ? format(parseISO(applicant.member.expiryDate1), "MMM dd, yyyy")
                : "—"}{" "}
            </div>
            <div className="col-2">
              {!applicant?.member?.expiryDate1 ||
              parseISO(applicant.member.expiryDate1) < new Date()
                ? "⚠"
                : ""}
              &nbsp;
            </div>
          </div>
          <div
            className="[ row ] [ details-section__item ]"
            style={{
              fontSize: "12px",
              fontWeight: "400",
            }}
          >
            <div className="col-6">License #:</div>
            <div className="col-5" style={{ fontWeight: "600" }}>
              {applicant?.member?.idDetail1 || "—"}
            </div>
            <div className="col-1"></div>
          </div>

          <div className="[ divider ]" />
          {/* Upload ID button */}
          <div className="text-center mt-4">
            <button
              className="btn"
              style={{
                fontSize: "14px",
                padding: "8px 24px",
                borderRadius: "4px",
                backgroundColor: "#e91e63",
                color: "white",
                border: "none",
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (fileInputRef.current) {
                  fileInputRef.current.click();
                }
              }}
            >
              Upload ID
            </button>
            {/* Hidden file input for direct file selection */}
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: "none" }}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file && selectedMember?.accountId && doUploadFile) {
                  // Automatically determine file type based on extension
                  const extension = file.name.split(".").pop()?.toLowerCase();
                  let fileType = "OTHER";

                  if (extension === "pdf") {
                    fileType = "PDF";
                  } else if (
                    ["jpg", "jpeg", "png", "gif", "bmp"].includes(extension)
                  ) {
                    fileType = "IMAGE";
                  }

                  // Upload the file with determined type
                  doUploadFile(
                    selectedMember.accountId,
                    file,
                    fileType,
                    "Secondary ID Document",
                    () => {
                      // Re-query file list after successful upload
                      if (doFileList) {
                        doFileList({
                          accountId: selectedMember.accountId,
                          page: 0,
                          size: 20,
                          fileType: null,
                        });
                      }
                    }
                  );

                  // Clear the input value so the same file can be selected again if needed
                  e.target.value = "";
                }
              }}
            />
          </div>
        </div>
      )}

      {/* Expiry Warning Modal */}
      <ExpiryWarningModal
        show={showExpiryWarningModal}
        onClose={() => setShowExpiryWarningModal(false)}
        idType={applicant?.member?.idType1}
        applicant={applicant}
      />

      {/* Edit Secondary ID Modal */}
      <EditSecondaryIdModal
        showSecondaryIdEditModal={showSecondaryIdEditModal}
        handleSecondaryIdModalClose={handleSecondaryIdModalClose}
        applicant={applicant}
        handleSecondaryIdSaveClick={handleSecondaryIdSaveClickWrapper}
        provinceList={provinceList}
        idTypes={idTypes}
        finInfo={finInfo}
      />
    </div>
  );
};
